/**
 * Pure nav-visibility gate shared by AppShell's sidebar and its quick-nav
 * search (the search list is built from the already-filtered sidebar
 * groups, so gating this one function gates both — see AppShell.tsx).
 *
 * Precedence, matching the pre-existing sidebar filter plus the new
 * per-item `permission` gate:
 *   1. warehouse: ONLY the Receiving item, unconditionally (3PL staff scope).
 *   2. A `permission`-gated item: tenant-side roles (owner/operator) always
 *      see it; a 'va' session needs that permission actually granted
 *      (me.user.permissions, itself a live DB read — see middleware/auth.ts
 *      and routes/auth.ts's buildMeResponse).
 *   3. Otherwise: the pre-existing `module` gate (or always-visible if the
 *      item has neither field).
 */
export interface NavGateItem {
  to: string;
  module?: string;
  /** Warehouse work destination. Warehouse users see these without a module
   * entitlement; tenant-side users need the warehouse module. */
  warehouseVisible?: boolean;
  /** Restrict a destination to the established tenant-side roles. */
  roles?: readonly string[];
  /** Grantable 'va' permission key that unlocks this item — currently just
   *  order planning. Ignored for every role except 'va'. */
  permission?: 'planning';
}

export interface NavGateContext {
  role: string | undefined;
  modules: Record<string, boolean> | undefined;
  /** va's granted permission keys; empty for every other role. */
  permissions: string[];
}

export function isNavItemVisible(item: NavGateItem, ctx: NavGateContext): boolean {
  if (ctx.role === 'warehouse')
    return item.warehouseVisible === true || item.to === '/app/receiving';
  if (item.warehouseVisible) {
    return (ctx.role === 'owner' || ctx.role === 'operator') && ctx.modules?.warehouse === true;
  }
  if (item.roles && !item.roles.includes(ctx.role ?? '')) return false;
  if (item.permission && ctx.role === 'va' && !ctx.permissions.includes(item.permission)) {
    return false;
  }
  return !item.module || ctx.modules?.[item.module] === true;
}
